/**
 * The Ιδιότητα/τίτλος categories, shared by the forms and the server.
 *
 * The client asked for a fixed list so the categories can be counted, with a
 * free-text escape under «Άλλο» for anyone who does not fit one.
 *
 * **The stored value is always the Greek label**, whatever language the form is
 * shown in. Storing the translated label would file an English sign-up as
 * "Employed" and a Greek one as "Εργαζόμενος" — the same person in two buckets.
 * Storing a code would read as gibberish in the Excel export the organisers
 * actually count from.
 *
 * CommonJS because the backend canonicalises on every write; job-title.js is the
 * ESM view the frontends import. One implementation, the same arrangement as
 * greek.cjs and event-calendar.cjs.
 */

/** Sentinel for the free-text branch. Never stored — the typed text is. */
const JOB_TITLE_OTHER = '__other__';

const JOB_TITLE_OPTIONS = [
  { value: 'Φοιτητής/Σπουδαστής', labelKey: 'register.jobTitleStudent' },
  { value: 'Απόφοιτος', labelKey: 'register.jobTitleGraduate' },
  { value: 'Εργαζόμενος', labelKey: 'register.jobTitleEmployed' },
  { value: 'Άνεργος', labelKey: 'register.jobTitleUnemployed' },
  { value: JOB_TITLE_OTHER, labelKey: 'register.jobTitleOther' },
];

const FIXED = JOB_TITLE_OPTIONS.filter((o) => o.value !== JOB_TITLE_OTHER).map((o) => o.value);

/**
 * Compare categories the way a person reads them, not byte for byte.
 *
 * Greek capitals drop the tonos, so the same word arrives as «Εργαζόμενος» from
 * the dropdown and «ΕΡΓΑΖΟΜΕΝΟΣ» from a spreadsheet — and one registration here
 * already stored exactly that. Compared exactly, those are two categories: the
 * record reopens under «Άλλο», and the organisers' count splits one bucket in
 * two, which is the one thing the dropdown exists to prevent.
 *
 * NFD then stripping combining marks removes the tonos and dialytika;
 * toLowerCase applies the final-sigma rule, so ΟΣ and ος meet. Whitespace
 * around the slash is collapsed because «Φοιτητής / Σπουδαστής» is how people
 * type it.
 */
function categoryKey(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, ' ')
    .trim();
}

const CANONICAL = new Map(FIXED.map((label) => [categoryKey(label), label]));

/**
 * The value to store: the canonical label for anything that *is* a category,
 * whatever its case or accents, and the text unchanged for anything that is
 * not. Empty stays empty — the field is optional.
 *
 * Applied on every write path, not just the forms, because the Excel import and
 * the admin's walk-in form write this column too, and spreadsheets are exactly
 * where the capitalised variants come from.
 */
function canonicalJobTitle(value) {
  if (value == null) return value;
  const trimmed = String(value).trim();
  if (!trimmed) return trimmed;
  return CANONICAL.get(categoryKey(trimmed)) || trimmed;
}

/**
 * Which option a stored value corresponds to.
 *
 * Anything that is not a category is «Άλλο» with the text kept — which is what
 * the existing free-text rows are, so reopening one shows the value rather than
 * losing it.
 */
function jobTitleSelection(stored) {
  const value = String(stored || '').trim();
  if (!value) return { choice: '', other: '' };
  const canonical = CANONICAL.get(categoryKey(value));
  if (canonical) return { choice: canonical, other: '' };
  return { choice: JOB_TITLE_OTHER, other: value };
}

module.exports = {
  JOB_TITLE_OTHER,
  JOB_TITLE_OPTIONS,
  categoryKey,
  canonicalJobTitle,
  jobTitleSelection,
};
